import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Button, TYPE } from '@hub/common/Button/Button';
import { Checkbox } from '@hub/common/Checkbox/Checkbox';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { Modal, ModalVariant } from '@hub/common/Modal/Modal';
import type { BoreholeAndChannelCheck } from './boreholeLimitations.utils';
import type { Procedure } from '@igus/iglidur-free-form-hub-interfaces';
import { createNumberFormatter } from '@icommerce/i18n';

type BoreholeAndChannelLimitationsModalProps = {
  visible: boolean;
  procedure: Procedure;
  limits: BoreholeAndChannelCheck['limits'];
  designerUrl: string;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
};

export const BoreholeAndChannelLimitationsModal = ({
  visible,
  procedure,
  limits,
  designerUrl,
  onClose,
  onConfirm,
}: BoreholeAndChannelLimitationsModalProps) => {
  const { t, i18n } = useTranslation('free-form-hub/freeFormHubDetails');
  const [accepted, setAccepted] = useState(false);

  const closeModal = () => {
    setAccepted(false);
    onClose();
  };

  const confirmAddToCart = () => {
    setAccepted(false);
    void onConfirm();
  };

  const formatLimit = (limit: number) => createNumberFormatter(i18n.language).format(limit);
  const boreholeLimit = formatLimit(limits.bores.warning);
  const channelLimit = formatLimit(limits.channels.warning);
  const limitsText =
    limits.bores.warning === limits.channels.warning
      ? t('BOREHOLE_LIMITATIONS.LIMITS_EQUAL', { procedure, limit: boreholeLimit })
      : t('BOREHOLE_LIMITATIONS.LIMITS_DIFFERENT', { procedure, boreholeLimit, channelLimit });

  return (
    <Modal
      variant={ModalVariant.DEFAULT}
      visible={visible}
      onClose={closeModal}
      headline={t('BOREHOLE_LIMITATIONS.TITLE')}
    >
      <div data-testid="borehole-limitations-modal" className="w-[calc(100vw-130px)] max-w-[730px] py-8">
        <div data-testid="borehole-limitations-information" className="text-sm font-normal leading-none">
          <div className="mb-8 flex items-center gap-4">
            <Icon id={IconID.WARNING} size={10} color={IconColor.ORANGE} />
            <h5 className="font-bold">{t('BOREHOLE_LIMITATIONS.SECTION_TITLE')}</h5>
          </div>

          <p className="mb-8">
            <span className="font-bold">{t('BOREHOLE_LIMITATIONS.RESULT_LABEL')}</span>{' '}
            {t('BOREHOLE_LIMITATIONS.RESULT')}
          </p>
          <p className="mb-8">{limitsText}</p>
          <p className="mb-8">{t('BOREHOLE_LIMITATIONS.REWORK_OPTIONS')}</p>
        </div>

        <div data-testid="borehole-limitations-approval" className="mb-8 text-sm leading-10">
          <Checkbox
            id="borehole-limitations-approval"
            value={accepted}
            label={t('BOREHOLE_LIMITATIONS.CHECKBOX_LABEL')}
            onChange={setAccepted}
          />
        </div>

        <div className="flex flex-col justify-between gap-6 md:flex-row">
          <Button
            testId="proceed-to-iglidur-designer"
            type={TYPE.PRIMARY}
            text={t('BOREHOLE_LIMITATIONS.PROCEED_TO_DESIGNER')}
            href={designerUrl}
            external
            className="md:w-[380px]"
            onClick={closeModal}
          />
          <Button
            testId="proceed-to-cart"
            type={TYPE.PRIMARY}
            text={t('BOREHOLE_LIMITATIONS.PROCEED_TO_CART')}
            disabled={!accepted}
            className="md:w-[380px]"
            onClick={confirmAddToCart}
          />
        </div>
      </div>
    </Modal>
  );
};
